import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  Button,
  CheckBox,
  Column,
  Icon,
  Input,
  Label,
  Modal,
  Row,
  Select,
  Tag,
} from '@drivesally/sally-ui';

import alert from '@drivesally/sally-ui/lib/libs/alert';
import parseError from '@drivesally/sally-fw/lib/error/parseError';

import {create as createDocumentApi} from '@drivesally/sally-fn/lib/document/api';
import driverState from '@drivesally/sally-fn/lib/driver/state';
import rentalState from '@drivesally/sally-fn/lib/rental/state';

import {darkGrey, green, grey} from '@drivesally/sally-ui/lib/libs/colors';

import driverRoute from '../../../components/pages/CustomerEditPage/route';
import {routeTicket} from '../../pages/CrmPage/route';
import CrmAircallLog from '../CrmAircallLog/CrmAircallLog';
import CrmConnectedAgentsBadge from '../CrmConnectedAgentsBadge/CrmConnectedAgentsBadge';
import CrmDriverDocPickerModal from '../CrmDriverDocPickerModal/CrmDriverDocPickerModal';
import CrmShortcutPicker from '../CrmShortcutPicker/CrmShortcutPicker';
import CrmSuggestedReplyPicker from '../CrmSuggestedReplyPicker/CrmSuggestedReplyPicker';
import CrmTicketConversationPanel from '../CrmTicketConversationPanel/CrmTicketConversationPanel';
import CrmTicketNotes from '../CrmTicketNotes/CrmTicketNotes';
import CrmTicketTitleEditor from '../CrmTicketTitleEditor/CrmTicketTitleEditor';
import CrmTagPill from '../CrmTagPill/CrmTagPill';
import resolveRental from '../../lib/resolveRental.lib.crm';
import getTeamLabel from '../../lib/getTeamLabel.lib.crm';
import renderShortcutMessage from '../../lib/renderShortcutMessage.lib.crm';
import ticketStates from '../../constants/ticketStates.const.crm';

const TagsList = styled.div`
  max-height: 180px;
  overflow-y: auto;
`;

const TagSearch = styled.div`
  margin-bottom: 8px;
`;

const TagRow = styled.div`
  align-items: center;
  border-bottom: 1px solid ${grey};
  cursor: pointer;
  display: flex;
  gap: 8px;
  padding: 6px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const TagsEmpty = styled.div`
  color: #888;
  font-size: 13px;
  padding: 8px 0;
`;

const SummaryText = styled.div`
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  max-height: 120px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const InfoWithTag = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const BranchTag = styled.span`
  color: ${darkGrey};
  font-size: 13px;
`;

const ModalLayout = styled.div`
  display: flex;
  max-height: calc(100vh - 200px);
  min-height: 480px;
`;

const DetailsColumn = styled.div`
  border-right: 1px solid ${grey};
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  padding: 16px;
`;

const ConversationColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  padding: 16px;
`;

const ConversationField = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const ConversationHeadingRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin: 0 0 12px;
`;

const ConversationHeading = styled.h3`
  color: #333;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const ConversationContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const ConnectRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px 0 0;
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0 0;
`;

const ComposerActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const ComposerTools = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 6px;
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  min-height: 32px;
  max-height: 160px;
  padding: 6px 10px;
  margin: 0;
  border: 2px solid ${grey};
  border-radius: 3px;
  outline: none;
  resize: none;
  overflow-y: auto;
  font-family: ProximaNova;
  font-size: 12px;
  line-height: 20px;
  color: #000;

  &:focus {
    border-color: ${darkGrey};
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const ChayaConvoId = styled.span`
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
`;

const CopyLinkButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 0;

  &:hover {
    color: ${darkGrey};
  }
`;

const CallLink = styled.a`
  align-items: center;
  background: ${green};
  border-radius: 3px;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  gap: 4px;
  padding: 2px 8px;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
  }
`;

const CopyLinkIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const UploadButton = styled.button`
  align-items: center;
  background: none;
  border: 1px solid ${grey};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;

  &:hover:not(:disabled) {
    background: ${grey};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  & svg {
    height: 16px;
    stroke: #555;
    width: 16px;
  }
`;

const UploadSpinner = styled.div`
  @keyframes crm-spin {
    to { transform: rotate(360deg); }
  }
  animation: crm-spin 0.7s linear infinite;
  border: 2px solid #ddd;
  border-radius: 50%;
  border-top-color: #555;
  height: 14px;
  width: 14px;
`;

const UploadMenu = styled.div`
  background: white;
  border: 1px solid ${grey};
  border-radius: 6px;
  bottom: calc(100% + 6px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  left: 0;
  min-width: 180px;
  position: absolute;
  z-index: 10;
`;

const UploadMenuItem = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: block;
  font-size: 13px;
  padding: 8px 12px;
  text-align: left;
  width: 100%;

  &:hover {
    background: ${grey};
  }

  &:first-child {
    border-radius: 6px 6px 0 0;
  }

  &:last-child {
    border-radius: 0 0 6px 6px;
  }
`;

const CrmTicketModal = ({
  aircallCalls,
  aircallLoading,
  assignedTeamId,
  chayaConvoId,
  connectedAgents,
  conversationLoading,
  conversationMessages,
  creatingNote,
  hasActiveDeactivation,
  loading,
  newNoteText,
  notes,
  notesLoading,
  onClose,
  onCreateNote,
  onDeleteNote,
  onNewNoteTextChange,
  onApplyShortcutTags,
  onRefreshSuggestedReplies,
  onSend,
  onSendFile,
  onStateChange,
  onTagToggle,
  onTeamChange,
  onTitleSave,
  onWsConnect,
  onWsDisconnect,
  saving,
  savingNoteIds,
  savingTitle,
  selectedTagIds,
  shortcuts,
  state,
  suggestedReplies,
  suggestedRepliesLoading,
  tags,
  teams,
  ticket,
  userData,
  visible,
  wsConnected,
}) => {
  const [inputText, setInputText] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docPickerVisible, setDocPickerVisible] = useState(false);
  const [pickerDriver, setPickerDriver] = useState(null);
  const fileInputRef = useRef(null);
  const messageTextareaRef = useRef(null);

  const ticketId = ticket?.id;

  useEffect(() => {
    if (!wsConnected) setInputText('');
  }, [wsConnected]);

  useEffect(() => {
    if (!visible) setTagSearch('');
  }, [visible, ticketId]);

  useEffect(() => {
    const el = messageTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [inputText]);

  const handleSend = () => {
    if (!inputText) return;
    onSend(inputText);
    setInputText('');
  };

  const handleMessageKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    handleSend();
  };

  const handleShortcutSelect = (shortcut) => {
    const message = shortcut?.message || '';
    if (message) {
      setInputText(
        renderShortcutMessage(message, {
          driver: ticket?.driver,
          ticket,
          user: userData,
        })
      );
    }
    if (onApplyShortcutTags) onApplyShortcutTags(shortcut);
  };

  const driverPhone = ticket?.driver?.phone_number;
  const telHref = (() => {
    const digits = `${driverPhone || ''}`.replace(/\D+/g, '');
    if (digits.length === 10) return `tel:+1${digits}`;
    if (digits.length === 11 && digits[0] === '1') return `tel:+${digits}`;
    return digits ? `tel:+${digits}` : null;
  })();

  const handleCopyLink = () => {
    if (!ticket?.id) return;
    const link = `${window.location.origin}${routeTicket(ticket.id)}`;
    navigator.clipboard.writeText(link);
    alert.info('Ticket link copied to clipboard');
  };

  const tagSearchQuery = tagSearch.trim().toLowerCase();
  const filteredTags = tagSearchQuery
    ? tags.filter((tag) =>
        `${tag.tag || ''}`.toLowerCase().includes(tagSearchQuery)
      )
    : tags;

  const handleOpenDocPicker = () => {
    setPickerDriver(ticket?.driver);
    setUploadMenuOpen(false);
    setDocPickerVisible(true);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !ticket?.driver?.id) return;
    const driverId = ticket.driver.id;

    setUploadMenuOpen(false);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document_file', file);
      formData.append('name', file.name);
      formData.append('type', 'crmUpload');
      formData.append('driver', driverId);
      formData.append('extra_data', JSON.stringify({}));
      const doc = await createDocumentApi(formData, true, {presignedUrlExpiry: 604800});
      onSendFile(doc);
    } catch (err) {
      const {message} = parseError(err);
      alert.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
  <>
  <Modal
    title={
      <TitleWrapper>
        <TitleRow>
          CRM Ticket #{ticket?.id || ''}
          <CopyLinkButton
            type="button"
            onClick={handleCopyLink}
            title="Copy ticket link"
          >
            <CopyLinkIcon />
          </CopyLinkButton>
          {!!ticket?.chaya_convo_id && (
            <ChayaConvoId>
              Chaya ID: {ticket.chaya_convo_id}
            </ChayaConvoId>
          )}
        </TitleRow>
        <CrmTicketTitleEditor
          key={ticket?.id || 'none'}
          title={ticket?.title}
          saving={savingTitle}
          wrap
          onSave={onTitleSave}
        />
      </TitleWrapper>
    }
    size="large"
    visible={visible}
    onClose={onClose}
    disableOutsideClickClose
  >
    {(Content) => (
      <Content padding="none" noBorder>
        <ModalLayout>
          <DetailsColumn>
            <Row margin>
              <Column size={1 / 2}>
                <Label label="Driver">
                  <InfoWithTag>
                    {ticket?.driver?.id ? (
                      <a
                        href={driverRoute(ticket.driver.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{color: 'inherit', textDecoration: 'none'}}
                      >
                        {ticket.sally_name || '-'}
                      </a>
                    ) : (
                      <span>{ticket?.sally_name || '-'}</span>
                    )}
                    {!!ticket?.driver?.sally_branch?.tag && (
                      <BranchTag>({ticket.driver.sally_branch.tag})</BranchTag>
                    )}
                    {!!ticket?.driver?.state && (
                      <Tag theme={driverState(ticket.driver.state).color}>
                        {driverState(ticket.driver.state).label}
                      </Tag>
                    )}
                    {!!hasActiveDeactivation && (
                      <Tag theme="red">Active Deactivation</Tag>
                    )}
                    {!!telHref && (
                      <CallLink href={telHref} title={`Call ${driverPhone}`}>
                        <Icon icon="phone" />
                        Call
                      </CallLink>
                    )}
                  </InfoWithTag>
                  {!!ticket?.driver?.id && (
                    <CrmAircallLog
                      key={ticket.id}
                      branch={ticket?.driver?.sally_branch}
                      calls={aircallCalls}
                      loading={aircallLoading}
                    />
                  )}
                </Label>
              </Column>
              <Column size={1 / 2}>
                <Label label="Rental">
                  <InfoWithTag>
                    <span>{resolveRental(ticket?.rental) || '-'}</span>
                    {!!ticket?.rental?.state && (
                      <Tag theme={rentalState(ticket.rental.state).color}>
                        {ticket.rental.state}
                      </Tag>
                    )}
                  </InfoWithTag>
                </Label>
              </Column>
            </Row>
            <Row margin>
              <Column size={1 / 2}>
                <Label label="State">
                  <Select
                    value={state}
                    options={ticketStates}
                    onChange={onStateChange}
                    disabled={saving || loading || state === 'closed'}
                    notFilterable
                  />
                </Label>
              </Column>
              <Column size={1 / 2}>
                <Label label="Assigned Team">
                  <Select
                    value={assignedTeamId}
                    options={[
                      {value: '', label: 'Unassigned'},
                      ...teams.map((team) => ({
                        value: `${team.id}`,
                        label: getTeamLabel(team),
                      })),
                    ]}
                    onChange={onTeamChange}
                    disabled={saving || loading}
                    notFilterable
                  />
                </Label>
              </Column>
            </Row>
            <Row margin>
              <Column>
                <Label label="Chaya Ticket Summary">
                  <SummaryText>
                    {ticket?.chaya_ticket_summary || '-'}
                  </SummaryText>
                </Label>
              </Column>
            </Row>
            <Row>
              <Column>
                <Label label="Tags">
                  <TagSearch>
                    <Input
                      value={tagSearch}
                      onChange={setTagSearch}
                      placeholder="Search tags"
                      disabled={saving || loading}
                      size="small"
                    />
                  </TagSearch>
                  <TagsList>
                    {!filteredTags.length ? (
                      <TagsEmpty>
                        {tagSearchQuery
                          ? 'No matching tags.'
                          : 'No tags available.'}
                      </TagsEmpty>
                    ) : (
                      filteredTags.map((tag) => (
                        <TagRow
                          key={tag.id}
                          onClick={
                            saving || loading
                              ? undefined
                              : onTagToggle(tag.id)
                          }
                        >
                          <CheckBox
                            value={selectedTagIds.includes(tag.id)}
                            onChange={() => {}}
                            disabled={saving || loading}
                            size="small"
                          />
                          <CrmTagPill
                            color={tag.color}
                            label={tag.tag}
                            small
                          />
                        </TagRow>
                      ))
                    )}
                  </TagsList>
                </Label>
              </Column>
            </Row>
            <Row>
              <Column>
                <CrmTicketNotes
                  creatingNote={creatingNote}
                  disabled={loading || saving}
                  newNoteText={newNoteText}
                  notes={notes}
                  notesLoading={notesLoading}
                  onCreateNote={onCreateNote}
                  onDeleteNote={onDeleteNote}
                  onNewNoteTextChange={onNewNoteTextChange}
                  savingNoteIds={savingNoteIds}
                />
              </Column>
            </Row>
          </DetailsColumn>
          <ConversationColumn>
            <ConversationField>
              <ConversationHeadingRow>
                <ConversationHeading>Driver Chat</ConversationHeading>
                <CrmConnectedAgentsBadge agents={connectedAgents} />
              </ConversationHeadingRow>
              <ConversationContent>
                {visible && (
                  <CrmTicketConversationPanel
                    emptyMessage={
                      chayaConvoId
                        ? undefined
                        : 'No conversation linked to this ticket'
                    }
                    loading={conversationLoading}
                    messages={conversationMessages}
                  />
                )}
              </ConversationContent>
              {!!chayaConvoId && (
                <CrmSuggestedReplyPicker
                  loading={suggestedRepliesLoading}
                  onRefresh={onRefreshSuggestedReplies}
                  onSelect={setInputText}
                  suggestions={suggestedReplies}
                />
              )}
              {chayaConvoId && !wsConnected && (
                <ConnectRow>
                  <Button theme="red" size="small" onClick={onWsConnect}>
                    Connect
                  </Button>
                </ConnectRow>
              )}
              {wsConnected && (
                <Composer>
                  <input ref={fileInputRef} type="file" style={{display: 'none'}} onChange={handleFileSelect} />
                  <MessageTextarea
                    innerRef={messageTextareaRef}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleMessageKeyDown}
                    placeholder="Type a message..."
                    rows={1}
                  />
                  <ComposerActions>
                    <ComposerTools>
                      <CrmShortcutPicker
                        shortcuts={shortcuts}
                        onSelect={handleShortcutSelect}
                      />
                      <div style={{position: 'relative'}}>
                        <UploadButton
                          disabled={uploading}
                          onClick={() => setUploadMenuOpen((o) => !o)}
                        >
                          {uploading ? (
                            <UploadSpinner />
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="17 8 12 3 7 8" />
                              <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                          )}
                        </UploadButton>
                        {uploadMenuOpen && (
                          <>
                            <div
                              style={{position: 'fixed', inset: 0, zIndex: 9}}
                              onClick={() => setUploadMenuOpen(false)}
                            />
                            <UploadMenu>
                              <UploadMenuItem onClick={handleOpenDocPicker}>
                                George Documents
                              </UploadMenuItem>
                              <UploadMenuItem onClick={() => { setUploadMenuOpen(false); fileInputRef.current.click(); }}>
                                Upload File
                              </UploadMenuItem>
                            </UploadMenu>
                          </>
                        )}
                      </div>
                    </ComposerTools>
                    <Button
                      theme="red"
                      size="small"
                      onClick={handleSend}
                      disabled={!inputText}
                    >
                      Send
                    </Button>
                    <Button theme="grey" size="small" onClick={onWsDisconnect}>
                      Disconnect
                    </Button>
                  </ComposerActions>
                </Composer>
              )}
            </ConversationField>
          </ConversationColumn>
        </ModalLayout>
      </Content>
    )}
  </Modal>
  <CrmDriverDocPickerModal
    driver={pickerDriver}
    visible={docPickerVisible}
    onClose={() => setDocPickerVisible(false)}
    onSend={onSendFile}
  />
  </>
  );
};

CrmTicketModal.propTypes = {
  aircallCalls: PropTypes.array,
  aircallLoading: PropTypes.bool,
  assignedTeamId: PropTypes.string,
  chayaConvoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  connectedAgents: PropTypes.array,
  conversationLoading: PropTypes.bool,
  conversationMessages: PropTypes.array,
  creatingNote: PropTypes.bool,
  hasActiveDeactivation: PropTypes.bool,
  loading: PropTypes.bool,
  newNoteText: PropTypes.string,
  notes: PropTypes.array,
  notesLoading: PropTypes.bool,
  onClose: PropTypes.func,
  onCreateNote: PropTypes.func,
  onDeleteNote: PropTypes.func,
  onNewNoteTextChange: PropTypes.func,
  onApplyShortcutTags: PropTypes.func,
  onRefreshSuggestedReplies: PropTypes.func,
  onSend: PropTypes.func,
  onSendFile: PropTypes.func,
  onStateChange: PropTypes.func,
  onTagToggle: PropTypes.func,
  onTeamChange: PropTypes.func,
  onTitleSave: PropTypes.func,
  onWsConnect: PropTypes.func,
  onWsDisconnect: PropTypes.func,
  saving: PropTypes.bool,
  savingNoteIds: PropTypes.array,
  savingTitle: PropTypes.bool,
  selectedTagIds: PropTypes.array,
  shortcuts: PropTypes.array,
  state: PropTypes.string,
  suggestedReplies: PropTypes.array,
  suggestedRepliesLoading: PropTypes.bool,
  tags: PropTypes.array,
  teams: PropTypes.array,
  ticket: PropTypes.object,
  userData: PropTypes.object,
  visible: PropTypes.bool,
  wsConnected: PropTypes.bool,
};

CrmTicketModal.defaultProps = {
  aircallCalls: [],
  hasActiveDeactivation: false,
  shortcuts: [],
  suggestedReplies: [],
};

export default CrmTicketModal;
